import React, { FunctionComponent, useState } from 'react';
import { Input } from '@vwfs-bronson/bronson-react';
import { FieldInputProps, useFormikContext } from 'formik';
import { t } from 'i18next';
import { FormField } from '../FormField/FormField';
import { FormFieldPropsBase } from './formfields.model';

type Props = {
  /**
   * - Handler to run on value change in onBlur handler.
   * - Will not run if input was interacted with but final value provided
   * was equal to the one existing before interaction.
   * - Will not run on programmatic change of the field value.
   */
  onValueChange?: (newValue: string, formValues: any) => void;
};

export type TextFormFieldProps = FormFieldPropsBase & Props;

export const TextFormField: FunctionComponent<TextFormFieldProps> = ({
  labelTranslationKey = '',
  placeholderTranslationKey = '',
  fieldName,
  isDisabled,
  isReadonly,
  optional,
  testId,
  infoTextTranslationKey,
  onBlur,
  onValueChange,
  onChange,
  addonSpinner,
}): React.JSX.Element => {
  const { values, setFieldTouched, setFieldValue } = useFormikContext<any>();
  const [oldValue, setOldValue] = useState('');

  const _onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    setFieldTouched(fieldName, true);
    onBlur?.(event);

    if (oldValue !== newValue) {
      setOldValue(newValue);
      onValueChange?.(newValue, values);
    }
  };

  const _onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(fieldName, event.target.value);
    onChange?.(event);
  };

  return (
    <FormField
      type="input"
      name={fieldName}
      required={!optional && !isReadonly}
      testId={testId ?? fieldName}
      labelText={`${t(labelTranslationKey)}`}
      infoText={infoTextTranslationKey ? t(infoTextTranslationKey) : ''}
      render={(fieldProps: FieldInputProps<any>) => (
        <Input
          {...fieldProps}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          placeholder={t(placeholderTranslationKey)}
          readOnly={isReadonly}
          value={values[fieldName] ?? ''}
          onBlur={_onBlur}
          onChange={_onChange}
          addonSpinner={addonSpinner}
        />
      )}
    />
  );
};
